/// ISO 8601 形式の日付 (branded 型 + フォーマット & 暦妥当性バリデーション)。
///
/// 対象フィールド: `shared/types/contract.ts` の `signed_on` / `starts_on` / `ends_on`、
/// `shared/types/product.ts` の `starts_on` / `ends_on` など。
///
/// バリデーション規則:
///   - `YYYY-MM-DD` (`^\d{4}-\d{2}-\d{2}$`) に厳密一致 (前後 whitespace もこれで弾く)
///   - 暦として妥当 (月 1-12、日がその月に対し妥当、閏年考慮) でなければ null
///   - 妥当なら入力文字列をそのまま保持 (verbatim・正規化なし)
///
/// 対応する backend VO は無い (frontend 定義)。日付は API 上 `string | null` として来るため、
/// frontend 側で構文 + 暦妥当性を担保する。
///
/// issue #469 / 親 issue #430。

/** 構文 + 暦妥当性を検証済みの `YYYY-MM-DD` 日付。 */
export type ISODate = string & { readonly __brand: 'ISODate' };

/** `YYYY-MM-DD` 形式の正規表現 (ASCII 数字のみ)。 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `value` を検証し、問題なければ `ISODate` 型で返す。
 *
 * 無効な場合は `null` を返す (例外は投げない)。
 *
 * - `YYYY-MM-DD` 形式 (ASCII 数字 4 桁 + `-` + 2 桁 + `-` + 2 桁) のみ有効。
 *   前後 whitespace・区切り文字違い・桁数違いは `^`/`$` で弾く。
 * - 暦として妥当でない日付 (`2026-02-30` / `2026-13-01` / `2025-02-29` 等) は無効。
 *   閏年も考慮 (`2024-02-29` は有効)。
 * - 妥当なら入力文字列をそのまま保持 (正規化なし)。
 */
export function createISODate(value: string): ISODate | null {
  // フォーマットチェック (前後 whitespace / 区切り違い / 桁数違いをここで弾く)
  if (!ISO_DATE_RE.test(value)) {
    return null;
  }

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));

  // 暦妥当性チェック: UTC で日付を組み立て、ロールオーバー (例 2026-02-30 → 03-02) が
  // 起きていないか各フィールドが入力と一致するかで検証する。閏年もこの方式で正しく判定される。
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return value as ISODate;
}
